import { useEffect, useMemo, useState } from 'react';
import {
  IconButton,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Textarea,
  Text,
  Box,
  VStack,
  HStack,
  Badge,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FaLifeRing } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import { getRecentActions } from '../../services/actionLog';
import { reportError } from '../../services/errorReporter';
import { useAuth } from '../../contexts/AuthContext';
import { getAdminSettings } from '../../services/settings';

export default function AdminSupportButton() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();
  const toast = useToast();
  const { user } = useAuth();
  const [extUI, setExtUI] = useState<string>('');
  const [extToken, setExtToken] = useState<string>('');

  const actions = useMemo(() => getRecentActions(12), [isOpen]);
  const path = location.pathname + location.search;
  const bg = useColorModeValue('gray.50', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.600');

  const timeline = useMemo(() => {
    const now = Date.now();
    return actions.map((a) => {
      const ago = Math.max(0, Math.round((now - a.at) / 1000));
      if (a.type === 'nav') return `-${ago}s NAV ${a.path}`;
      return `-${ago}s ${a.method.toUpperCase()} ${a.url} ${a.status ?? ''} ${a.ms ? a.ms + 'ms' : ''}`.trim();
    });
  }, [actions]);

  // Load external error-review UI/token when modal opens
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const s = await getAdminSettings();
        setExtUI(String((s as any).error_review_ui_url || ''));
        setExtToken(String((s as any).error_review_admin_token || ''));
      } catch {}
    })();
  }, [isOpen]);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await reportError({
        severity: 'warn',
        message: message?.trim() ? `Support: ${message.trim()}` : 'Support: bez popisu',
        tags: { type: 'support', source: 'admin' },
        context: { path, recentActions: actions },
        user_id: (user as any)?.id,
        url: path,
      });
      toast({ title: 'Odesláno', description: 'Děkujeme, ozveme se co nejdříve.', status: 'success', duration: 3000 });
      setMessage('');
      onClose();
    } catch {
      toast({ title: 'Chyba', description: 'Nepodařilo se odeslat.', status: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Box position="fixed" right={4} bottom={4} zIndex={40}>
        <Tooltip label="Zákaznická podpora" hasArrow>
          <IconButton
            aria-label="Zákaznická podpora"
            icon={<FaLifeRing />}
            onClick={onOpen}
            colorScheme="blue"
            borderRadius="full"
            boxShadow="lg"
          />
        </Tooltip>
      </Box>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Nahlásit problém</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <HStack spacing={3}>
                <Badge colorScheme="blue">Admin</Badge>
                <Text fontSize="sm" color="gray.500">{path}</Text>
              </HStack>
              <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="md" p={3} maxH="160px" overflowY="auto">
                <Text fontSize="xs" color="gray.500" mb={2}>Poslední akce</Text>
                <VStack align="stretch" spacing={1}>
                  {timeline.map((t, i) => (
                    <Text key={i} fontFamily="mono" fontSize="xs">{t}</Text>
                  ))}
                </VStack>
              </Box>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Popište, co se stalo…"
                rows={5}
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <VStack align="stretch" spacing={2} flex={1} mr={3}>
              <HStack>
                <Button size="sm" variant="outline" onClick={() => window.open('/admin/docs', '_blank')}>Nápověda</Button>
              </HStack>
            </VStack>
            <Button colorScheme="blue" onClick={submit} isLoading={submitting}>Odeslat</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
